import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { openManagedMatDialog } from '@go-modules/utilities';
import { UniqueIdGeneratorUtil } from '@go-modules/utilities/unique-id-generator/unique-id-generator.util';
import { BehaviorSubject, Subscription, interval } from 'rxjs';
import { takeWhile } from 'rxjs/operators';

export interface RateLimitDialogData {
	retryAfter: number;
	dialogTitleId?: string;
}

@Component({
	selector: 'rate-limit-dialog',
	templateUrl: './rate-limit-dialog.component.html',
	styleUrls: ['./rate-limit-dialog.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class RateLimitDialogComponent implements OnDestroy, OnInit {
	public retryAfter: number;
	public countdown$: BehaviorSubject<number>;
	private subscription: Subscription;

	public static open (
		dialog: MatDialog,
		config?: MatDialogConfig<RateLimitDialogData>
	): MatDialogRef<RateLimitDialogComponent, void> {
		const dialogTitleId = config?.data?.dialogTitleId ?? RateLimitDialogComponent.getDialogTitleId();

		return openManagedMatDialog(dialog, RateLimitDialogComponent, {
			...(config ?? {}),
			data: {
				...(config?.data ?? {}),
				dialogTitleId
			},
			ariaLabelledBy: dialogTitleId
		});
	}

	private static getDialogTitleId (): string {
		return `rate-limit-dialog-title-${UniqueIdGeneratorUtil.getId()}`;
	}

	constructor (
		@Inject(MAT_DIALOG_DATA) public data: RateLimitDialogData,
		private dialogRef: MatDialogRef<RateLimitDialogComponent>
	) {
		this.dialogRef.disableClose = true;
		this.data.dialogTitleId ??= RateLimitDialogComponent.getDialogTitleId();
		this.retryAfter = this.data.retryAfter / 1000;
		this.countdown$ = new BehaviorSubject<number>(this.retryAfter);
	}

	public ngOnInit () {
		this.startCountdown();
	}

	public ngOnDestroy () {
		if (this.subscription) {
			this.subscription.unsubscribe();
		}
	}

	private startCountdown () {
		this.subscription = interval(1000).pipe(
			takeWhile(() => this.countdown$.getValue() > 0)
		).subscribe({
			next: () => {
				this.countdown$.next(this.countdown$.getValue() - 1);
			},
			complete: () => this.dialogRef.close()
		});
	}
}
